import type {
  McpCatalogResponseDto,
  TestMcpConnectionResponseDto,
} from '@inference.engine/api-client/web';
import { useQueries } from '@tanstack/react-query';
import { useMemo } from 'react';
import { mcpHealthQueryOptions } from './mcpHealthQuery';
import { useBuiltInMcpServers } from './useBuiltInMcpServers';
import { useInstallableCatalog } from './useInstallableCatalog';
import { useInstalledMcps } from './useInstalledMcps';

type AuthType = McpCatalogResponseDto['authType'];
type ConnectionErrorType = NonNullable<TestMcpConnectionResponseDto['errorType']>;

export type AgentMcpItemStatus = 'connected' | 'error' | 'checking' | 'not_installed';

/** One MCP assigned to an agent, resolved to the current user's connection status. */
export interface AgentMcpItem {
  mcpCatalogId: string;
  name: string;
  /** Description, shown as the row subtitle. */
  description?: string;
  /** Catalog auth type; absent for built-in platform servers (which are never user-managed). */
  authType?: AuthType;
  /** Built-in platform server — always available, no install/auth action. */
  builtIn: boolean;
  installed: boolean;
  status: AgentMcpItemStatus;
  errorType?: ConnectionErrorType;
  errorMessage?: string;
}

type HealthQuery =
  | {
      isPending: boolean;
      isFetching: boolean;
      isError: boolean;
      data?: TestMcpConnectionResponseDto;
    }
  | undefined;

/** Map a live `/test` query to the row's status fields (built-in and catalog rows share this). */
const resolveStatus = (
  health: HealthQuery,
): Pick<AgentMcpItem, 'status' | 'errorType' | 'errorMessage'> => {
  const data = health?.data;
  // `isFetching` (not just `isPending`) so a manual retry — which refetches an already-loaded
  // query — also shows "checking", giving the user feedback that the re-check is running.
  if (health?.isPending || health?.isFetching) return { status: 'checking' };
  if (data?.status === 'error' || health?.isError) {
    return { status: 'error', errorType: data?.errorType, errorMessage: data?.errorMessage };
  }
  return { status: 'connected' };
};

export interface UseAgentMcpStatusResult {
  /** The agent's MCPs: built-in platform servers first, then catalog MCPs (stale ids dropped). */
  items: AgentMcpItem[];
  /** True while the base catalog/install/available lists are still loading. */
  isLoading: boolean;
  /** True if any *user-actionable* item is not installed or failing — drives the warning dot. */
  needsAttention: boolean;
}

/**
 * Resolves the MCP server ids assigned to an agent into per-MCP connection status for the
 * current user, for the chat composer's MCP flyout.
 *
 * Contract (see `useAgentMcpStatus.test.tsx`):
 *  - Built-in (platform) ids — those in `useBuiltInMcpServers` — resolve to always-available rows
 *    (`installed: true`, no auth action), shown first.
 *  - Remaining ids that exist in the catalog (`useInstallableCatalog`) resolve to catalog rows;
 *    ids in neither list (stale) are dropped.
 *  - `installed` = the id is in the user's installs (`useInstalledMcps`, matched on `mcpCatalogId`).
 *  - Eager live health for rows with a resolvable runtime server (built-in + installed catalog):
 *    a non-installed catalog MCP is `not_installed`; otherwise `checking` while its `/test` is in
 *    flight, then `connected` / `error` (with `errorType`/`errorMessage`).
 *  - `needsAttention` = any *catalog* item is `not_installed` or `error` (built-in failures aren't
 *    fixable from here, so they don't raise the dot).
 *  - `isLoading` = catalog, installs, or built-in list still loading.
 */
export function useAgentMcpStatus(mcpServerIds: string[]): UseAgentMcpStatusResult {
  // No assigned ids → don't touch the (flag-gated) catalog/installed/built-in endpoints at all.
  const enabled = mcpServerIds.length > 0;
  const { data: catalog, isLoading: catalogLoading } = useInstallableCatalog(enabled);
  const { data: installed, isLoading: installedLoading } = useInstalledMcps(enabled);
  const { data: builtIn, isLoading: builtInLoading } = useBuiltInMcpServers(enabled);

  const assigned = useMemo(() => new Set(mcpServerIds), [mcpServerIds]);

  // Built-in (platform) servers assigned to the agent: always available, not user-managed.
  const assignedBuiltIn = useMemo(
    () => (builtIn ?? []).filter((server) => assigned.has(server.id)),
    [builtIn, assigned],
  );

  // Catalog MCPs assigned to the agent (built-in and stale ids excluded).
  const assignedCatalog = useMemo(
    () => (catalog ?? []).filter((entry) => assigned.has(entry.id)),
    [catalog, assigned],
  );

  // Of the catalog ones, those the current user has actually installed (matched on mcpCatalogId).
  const installedAssigned = useMemo(() => {
    const ids = new Set(assignedCatalog.map((entry) => entry.id));
    return (installed ?? []).filter((inst) => ids.has(inst.mcpCatalogId));
  }, [installed, assignedCatalog]);

  // Live health: one /test per row with a resolvable runtime server — every built-in plus every
  // installed catalog MCP (non-installed catalog MCPs have no server to test, so they're skipped).
  const liveIds = useMemo(
    () => [
      ...assignedBuiltIn.map((server) => server.id),
      ...installedAssigned.map((inst) => inst.mcpCatalogId),
    ],
    [assignedBuiltIn, installedAssigned],
  );

  // `useQueries` fans out a variable number of queries (can't call a hook in a loop). Each query is
  // built from the shared `mcpHealthQueryOptions`, so it matches `useTestMcpConnection` exactly.
  const healthQueries = useQueries({ queries: liveIds.map(mcpHealthQueryOptions) });

  // Computed each render (cheap): `healthQueries` isn't referentially stable, so memoizing on it
  // buys nothing and trips the tanstack-query lint rule.
  const healthById = new Map(liveIds.map((id, idx) => [id, healthQueries[idx]]));

  const builtInItems: AgentMcpItem[] = assignedBuiltIn.map((server) => ({
    mcpCatalogId: server.id,
    name: server.name,
    description: server.description ?? undefined,
    builtIn: true,
    installed: true,
    ...resolveStatus(healthById.get(server.id)),
  }));

  const catalogItems: AgentMcpItem[] = assignedCatalog.map((entry) => {
    const base = {
      mcpCatalogId: entry.id,
      name: entry.name,
      description: entry.description,
      authType: entry.authType,
      builtIn: false as const,
    };
    if (!installedAssigned.some((inst) => inst.mcpCatalogId === entry.id)) {
      return { ...base, installed: false, status: 'not_installed' };
    }
    return { ...base, installed: true, ...resolveStatus(healthById.get(entry.id)) };
  });

  const items = [...builtInItems, ...catalogItems];

  return {
    items,
    isLoading: catalogLoading || installedLoading || builtInLoading,
    needsAttention: catalogItems.some(
      (item) => item.status === 'not_installed' || item.status === 'error',
    ),
  };
}
